import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { Component, computed, input, Input } from '@angular/core';

import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MultiTooltipDirective } from '@shared/directives/multi-tooltip.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import { ErrorsTooltipComponent } from '../errors-tooltip/errors-tooltip.component';

export interface AuxButtonGroup {
  disabled?: boolean;
  disabledTooltip?: string;
  disabledTooltips?: string[];
  disabledTooltipsTitle?: string;
  iconClass?: string;
  iconName?: AuxIconName;
  name: string;
  onClick: () => Promise<void> | void;
  pendoId?: string;
  show?: boolean;
}

@Component({
  imports: [
    IconComponent,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    TooltipDirective,
    NgClass,
    ErrorsTooltipComponent,
    MultiTooltipDirective,
  ],
  selector: 'aux-button-group',
  styles: [],
  template: `
    <button
      class="relative flex items-center py-[7px] pl-[10px] pr-[8px] leading-7"
      [ngClass]="buttonClass()"
      (click)="dropdownOpened = !dropdownOpened"
      [disabled]="disabled || loading"
      [auxTooltip]="disabled ? disabledTooltip : ''"
      [auxMultiTooltip]="disabledMultiTooltip()"
      [multiTooltipTitle]="disabledMultiTooltipTitle()"
    >
      @if (iconName && !loading) {
        <aux-icon [name]="iconName!" [ngClass]="{ 'mr-[5px]': !!label }" />
      }
      @if (label) {
        <div>{{ label }}</div>
      }

      @if (loading) {
        <span class="relative right-[-12px] flex -translate-x-1/2 transform">
          <span class="spinner h-5 w-5"></span>
        </span>
      }

      @if (showDropdownIcon() && !loading) {
        <aux-icon name="ChevronDown" class="ml-[5px]" />
      }
    </button>

    <div #trigger="cdkOverlayOrigin" cdkOverlayOrigin></div>

    <ng-template
      cdkConnectedOverlay
      class="border-solid border-aux-gray-dark-100"
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="dropdownOpened"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'random-class'"
      (detach)="dropdownOpened = false"
      (backdropClick)="dropdownOpened = false"
      [cdkConnectedOverlayPositions]="position"
    >
      <div class="mt-2 min-w-[200px] rounded bg-white shadow-[0_2px_4px_0_rgba(24,30,60,0.3)]">
        @for (option of options; track option.name) {
          @if (option.hasOwnProperty('show') ? option.show : true) {
            @let tooltipText =
              option.disabled ? option.disabledTooltip || option.disabledTooltips?.[0] || '' : '';

            @let templateTooltip =
              option.disabled && (option.disabledTooltips?.length || 0) > 1
                ? errorArrayTooltip
                : undefined;

            <div
              (click)="onOptionClick(option)"
              class="flex items-center p-[8px]"
              [ngClass]="{
                'cursor-pointer hover:bg-aux-blue-light-50': option.disabled !== true,
                'cursor-not-allowed opacity-50': option.disabled === true,
              }"
              auxTooltip
              [auxTooltip]="templateTooltip ? '' : tooltipText"
              [template]="templateTooltip"
              [attr.data-pendo-id]="option.pendoId"
            >
              @if (option.iconName) {
                <aux-icon [name]="option.iconName" [ngClass]="option.iconClass" class="mr-[12px]" />
              }
              {{ option.name }}
            </div>

            <ng-template #errorArrayTooltip>
              @if (!!option.disabledTooltips) {
                <aux-errors-tooltip
                  [errors]="option.disabledTooltips"
                  [title]="option.disabledTooltipsTitle || ''"
                />
              }
            </ng-template>
          }
        }
      </div>
    </ng-template>
  `,
})
export class ButtonGroupComponent {
  defaultClasses = {
    custom: '',
    hyperlink: 'btn--hyperlink',
    negative: 'btn--negative',
    primary: 'btn--primary',
    secondary: 'btn--secondary',
    success: 'btn--success',
  };

  buttonClass = computed(() => this.defaultClasses[this.variant()]);

  disabledMultiTooltip = input<string[]>([]);
  disabledMultiTooltipTitle = input<string>('');
  showDropdownIcon = input<boolean>(true);
  variant = input<'custom' | 'hyperlink' | 'negative' | 'primary' | 'secondary' | 'success'>(
    'secondary'
  );

  @Input() disabled = false;
  @Input() disabledTooltip = '';
  dropdownOpened = false;
  @Input() iconName?: AuxIconName;
  @Input() label = '';
  @Input() loading = false;
  @Input() options: AuxButtonGroup[] = [];
  @Input() position: ConnectedPosition[] = [];

  onOptionClick(option: AuxButtonGroup) {
    if (!option.disabled) {
      void option.onClick();
      this.dropdownOpened = false;
    }
  }
}
